import React, { useContext } from 'react';
import type { AnsweringServicePayer } from '@/app-package';
import { AppointmentDeskSVG } from '@bb/elements-web';
import { AnsweringServiceType } from '@bb/domain/dist/types/AnsweringSubscription';
import useOverduePaymentStage from 'hooks/useOverduePaymentStage';
import { AuthContext } from 'contexts';
import { OrgStatus } from '../../../../../onboarding/constants';
import SubscriptionCard, {
  SubscriptionCardConfigType,
} from './SubscriptionCard';
import { ViewingLevel } from '../../..';

export interface AnsweringServiceCardProps {
  /** Whether there was an error fetching the Answering Service subscription data. */
  fetchDataError: boolean;
  /** Whether the user is subscribed to this plan */
  isSubscribed: boolean;
  /** What type of Answering Service this user has */
  answeringServiceType: AnsweringServiceType;
  /** How the subscription is billed - affects text next to status pill */
  answeringServicePayer: AnsweringServicePayer;
  /** Callback when button is clicked */
  onButtonClick?: () => void;
  /** Whether this is an indivdual user or for a brokerage viewing the page */
  viewingLevel: ViewingLevel;
}

interface SuspensionValues {
  isSuspendedVal: boolean;
  suspensionTextVal: string;
}

/** Determine the suspended values we will add to the config. */
const getSuspensionValues = (
  isUserSuspended: boolean,
  isBrokerageSuspended: boolean,
  answeringServicePayer: AnsweringServicePayer,
  viewingLevel: ViewingLevel,
): SuspensionValues => {
  // If a keyholder has not paid for their own subscriptions, return that.
  if (answeringServicePayer === 'user' && isUserSuspended) {
    return {
      isSuspendedVal: true,
      suspensionTextVal:
        'This subscription has been suspended due to outstanding payments. Please update your card information.',
    };
  }
  // If a brokerage has not paid for their own subscriptions, return that.
  if (answeringServicePayer === 'brokerage' && isBrokerageSuspended) {
    return {
      isSuspendedVal: true,
      suspensionTextVal:
        viewingLevel === 'user'
          ? 'This subscription has been suspended. Please contact your brokerage for more information.'
          : "This subscription has been suspended due to outstanding payments. Please update your brokerage's card information.",
    };
  }
  // Otherwise, everyone is in good standing.
  return {
    isSuspendedVal: false,
    suspensionTextVal: '',
  };
};

/** The config we pass in depending on which type of Answering Service card to show */
const answeringServiceConfig: Record<
  AnsweringServiceType,
  SubscriptionCardConfigType
> = {
  AppointmentDesk: {
    planName: 'Appointment Desk',
    description:
      'A US and Canada-based live answering service that manages incoming calls for your listings.',
    statusText: 'Upgrade Available',
    isUpgradeAvailable: true,
  },
  LiveConciergePlus: {
    planName: 'Live Concierge Service',
    description:
      'A US and Canada-based live answering service that manages all incoming and outgoing calls for your listings.',
    statusText: 'Active',
    isUpgradeAvailable: false,
  },
  Legacy: {
    planName: '',
    description: '',
    statusText: '',
    isUpgradeAvailable: false,
  },
};

/** The text to show next to the status label */
const billingTypeLabels: Record<AnsweringServicePayer, string> = {
  boardOrMls: 'Managed by Organization',
  brokerage: 'Managed by Brokerage',
  user: '',
};

/**
 * The card displaying information about the Answering Service
 * that the user has access to.
 */
const AnsweringServiceCard: React.FC<AnsweringServiceCardProps> = ({
  fetchDataError,
  isSubscribed = false,
  answeringServiceType = AnsweringServiceType.LiveConciergePlus,
  // If no explicit payer is passed in, we default to 'user'
  // to allow a user or brokerage to pay for it themself.
  answeringServicePayer = 'user',
  onButtonClick,
  viewingLevel,
}) => {
  const handleClick = () => {
    onButtonClick?.();
  };

  const { auth } = useContext(AuthContext);

  const { isUserSuspended, isOrgSuspended: isBrokerageSuspended } =
    useOverduePaymentStage();

  const suspensionValues = getSuspensionValues(
    isUserSuspended,
    isBrokerageSuspended,
    answeringServicePayer,
    viewingLevel,
  );

  const config = answeringServiceConfig[answeringServiceType];
  config.isSuspended = suspensionValues.isSuspendedVal;
  config.suspensionTooltipText = suspensionValues.suspensionTextVal;
  const managedByText = billingTypeLabels[answeringServicePayer];
  const orgStatus = auth.org.orgCustomerSuccess.status as OrgStatus;

  return (
    <SubscriptionCard
      fetchDataError={fetchDataError}
      orgStatus={orgStatus}
      isSubscribed={isSubscribed}
      statusText={config.statusText}
      image={<AppointmentDeskSVG />}
      isUpgradeAvailable={config.isUpgradeAvailable}
      isSuspended={config.isSuspended}
      suspensionTooltipText={config.suspensionTooltipText}
      allowedToSelfUpgrade
      managedByText={managedByText}
      upgradeText="Subscribe to Live Concierge"
      planName={config.planName}
      description={config.description}
      onButtonClick={handleClick}
    />
  );
};

export default AnsweringServiceCard;
